import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  EventEmitter,
  Inject,
  inject,
  input,
  Input,
  OnChanges,
  Output,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { isPlatformServer, NgForOf, NgIf } from '@angular/common';
import { Router } from '@angular/router';

import { ImageLoadingComponent } from '@common/image-loading/image-loading.component';
import { SlideItem } from '@common/swiper/model';
import { SwiperDirective } from '@helper/swiper.directive';
import { FocusRemoverDirective } from '@helper/focus-remover.directive';
import { Links } from '@helper/links';

import { SwiperOptions } from 'swiper/types';
import { Autoplay, Keyboard, Navigation, Pagination } from 'swiper/modules';
import { SwiperContainer } from 'swiper/swiper-element';

@Component({
  selector: 'app-swiper-slides',
  templateUrl: './swiper-slides.component.html',
  styleUrls: ['./swiper-slides.component.scss'],
  imports: [NgForOf, ImageLoadingComponent, NgIf, SwiperDirective, FocusRemoverDirective],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SwiperSlidesComponent implements OnChanges {
  readonly isServer = isPlatformServer(this.platformId);
  readonly width = input.required<number>();
  readonly aspectRatio = input(0);
  readonly targetHeight = computed(() => this.width() * this.aspectRatio());

  private readonly router = inject(Router);

  constructor(@Inject(PLATFORM_ID) private platformId: object) {}

  @Output() readonly itemClick = new EventEmitter<SlideItem>();

  @Input({ required: true }) slides: SlideItem[] = [];
  @Input() widthCss = '';
  @Input() hideBullet = false;
  @Input() smallBullet = false;
  @Input() pagination = true;
  @Input() navigation = true;
  @Input() autoPlay = true;
  @Input() initialSlide = 0;
  @Input() allowTouchMove = true;

  @ViewChild('swiper') swiper?: ElementRef<SwiperContainer>;

  slideIds = '';
  config: SwiperOptions | undefined = undefined;

  ngOnChanges(): void {
    this.slideIds = this.slides.map(s => s.hash).join(',');
    this.config = {
      modules: [Navigation, Pagination, Autoplay, Keyboard],
      pagination: {
        clickable: true,
      },
      navigation: this.navigation,
      loop: this.slides.length > 1,
      keyboard: true,
      allowTouchMove: this.allowTouchMove,
      autoplay: this.autoPlay
        ? {
            delay: 6000,
            disableOnInteraction: false,
          }
        : false,
      initialSlide: this.initialSlide,
    };
  }

  onClick(item: SlideItem): void {
    this.itemClick.emit(item);
    Links.handleLink(this.router, item.link);
  }

  onPrev(): void {
    this.swiper?.nativeElement?.swiper.slidePrev();
  }

  onNext(): void {
    this.swiper?.nativeElement?.swiper.slideNext();
  }

  trackByFn(_: number, item: SlideItem): string {
    return item.hash;
  }
}
